// Helper para decodificar e validar JWT
// Validacao de expiracao ATIVADA

/**
 * Decodifica um token JWT e retorna o payload
 * @param {string} token - Token JWT
 * @returns {object|null} - Payload decodificado ou null se invalido
 */
export const decodeJWT = (token) => {
  try {
    if (!token) return null;

    // JWT tem 3 partes separadas por ponto: header.payload.signature
    const parts = token.split('.');

    if (parts.length !== 3) return null;

    // Decodificar a parte do payload (segunda parte)
    const payload = parts[1];

    // Base64 decode (substituir caracteres URL-safe)
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join('')
    );

    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
};

/**
 * Verifica se o token esta expirado
 * @param {string} token - Token JWT (opcional, usa localStorage se nao fornecido)
 * @returns {boolean} - true se expirado, false se valido
 */
export const isTokenExpired = (token) => {
  try {
    const tokenToCheck = token || localStorage.getItem('sessionToken');

    if (!tokenToCheck) {
      console.warn('[JWT] isTokenExpired: sem token');
      return true;
    }

    const decoded = decodeJWT(tokenToCheck);

    if (!decoded) {
      console.warn('[JWT] isTokenExpired: nao conseguiu decodificar token');
      return true;
    }

    let expirationMs;

    if (decoded.exp) {
      // Verificar se exp e Unix timestamp ou Delphi DateTime
      // Unix timestamp para datas atuais e > 1_000_000_000 (Sep 2001)
      // Delphi DateTime para datas atuais e ~46_000 (dias desde 30/12/1899)
      if (decoded.exp > 1000000000) {
        // Formato Unix timestamp padrao (segundos)
        expirationMs = decoded.exp * 1000;
      } else {
        // Formato Delphi DateTime (dias desde 30/12/1899) armazenado no campo exp
        console.warn('[JWT] Token com exp em formato Delphi DateTime:', decoded.exp);
        const delphiEpoch = new Date(1899, 11, 30).getTime();
        expirationMs = delphiEpoch + (decoded.exp * 24 * 60 * 60 * 1000);
      }
    } else if (decoded.Expiration) {
      // Campo Expiration explicito (Delphi DateTime)
      const delphiEpoch = new Date(1899, 11, 30).getTime();
      expirationMs = delphiEpoch + (decoded.Expiration * 24 * 60 * 60 * 1000);
    } else {
      console.warn('[JWT] isTokenExpired: token sem campo exp nem Expiration. Claims:', Object.keys(decoded));
      return true;
    }

    const currentTime = Date.now();
    // Margem de 30 segundos para evitar problemas de sincronizacao
    const isExpired = currentTime > (expirationMs - 30000);

    if (isExpired) {
      const expiredAgo = Math.round((currentTime - expirationMs) / 1000);
      console.warn(`[JWT] Token EXPIRADO ha ${expiredAgo}s. Exp: ${new Date(expirationMs).toISOString()}`);
    }

    return isExpired;
  } catch (e) {
    console.error('[JWT] isTokenExpired: erro ao verificar token:', e.message);
    return true;
  }
};

/**
 * Verifica se o token e valido (formato e expiracao)
 * @param {string} token - Token JWT
 * @returns {boolean} - true se valido, false se invalido
 */
export const isTokenValid = (token) => {
  if (!token) {
    console.warn('[JWT] isTokenValid: sem token');
    return false;
  }

  // Verificar se tem 3 partes
  const parts = token.split('.');
  if (parts.length !== 3) {
    console.warn('[JWT] isTokenValid: token nao tem 3 partes');
    return false;
  }

  // Verificar expiracao
  if (isTokenExpired(token)) {
    console.warn('[JWT] isTokenValid: token expirado');
    return false;
  }

  // Verificar se consegue decodificar
  const decoded = decodeJWT(token);
  if (!decoded) {
    console.warn('[JWT] isTokenValid: nao conseguiu decodificar');
    return false;
  }

  // Verificar campos obrigatorios (suporta diferentes formatos)
  const hasUserId = decoded.ID_USUARIO || decoded.id_usuario ||
                    decoded.idusuario || decoded.sub || decoded.id;
  if (!hasUserId) {
    console.warn('[JWT] isTokenValid: token sem campo de usuario. Claims:', Object.keys(decoded));
    return false;
  }

  return true;
};

/**
 * Retorna o tempo restante do token em millisegundos
 * @param {string} token - Token JWT (opcional, usa localStorage se nao fornecido)
 * @returns {number} - Tempo restante em ms ou 0 se expirado
 */
export const getTokenRemainingTime = (token) => {
  try {
    const tokenToCheck = token || localStorage.getItem('sessionToken');
    const decoded = decodeJWT(tokenToCheck);

    if (!decoded) return 0;

    let expirationTime;

    if (decoded.exp) {
      expirationTime = decoded.exp * 1000;
    } else if (decoded.Expiration) {
      // Formato Delphi DateTime
      const delphiEpoch = new Date(1899, 11, 30).getTime();
      expirationTime = delphiEpoch + (decoded.Expiration * 24 * 60 * 60 * 1000);
    } else {
      return 0;
    }

    const remainingMs = expirationTime - Date.now();
    return Math.max(0, remainingMs);
  } catch (e) {
    return 0;
  }
};

/**
 * Retorna o tempo restante formatado (ex: "2h 30m")
 * @param {string} token - Token JWT (opcional)
 * @returns {string} - Tempo formatado ou "Expirado"
 */
export const getTokenRemainingTimeFormatted = (token) => {
  const remainingMs = getTokenRemainingTime(token);

  if (remainingMs <= 0) return 'Expirado';

  const hours = Math.floor(remainingMs / (1000 * 60 * 60));
  const minutes = Math.floor((remainingMs % (1000 * 60 * 60)) / (1000 * 60));

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};

/**
 * Limpa todos os dados de sessao do localStorage.
 *
 * Precisa cobrir TUDO que o login grava, senao o dado de uma conta sobrevive
 * ao logout e aparece na proxima. Ja aconteceu: o `sessionVersao` e os caches
 * de `config*` ficavam para tras.
 */
export const clearSession = () => {
  // Limpeza POR PREFIXO, de proposito.
  //
  // Antes isto era uma lista escrita a mao, e ela apodreceu: ficou sem
  // `sessionVersao`, `sessionPrefs`, `sessionImpersonating`,
  // `sessionImpersonationMeta` e sem nenhum dos caches `config*`. Toda chave
  // esquecida e dado de uma conta sobrevivendo ao logout e reaparecendo na
  // proxima — foi assim que o aviso de "periodo de teste" de uma conta em
  // avaliacao apareceu numa conta antiga.
  //
  // Com prefixo, chave nova nasce coberta:
  //   session*  -> tudo que identifica a sessao (token, refresh, loja, versao,
  //                impersonation, preferencias do usuario)
  //   config*   -> caches de configuracao DO CLIENTE (ConfigsContext e
  //                configFerramentas espelham em localStorage)
  //
  // Fica de fora `gridColumns_*`, que ja e chaveado por id de usuario e por
  // isso nao vaza, e chaves de outros apps na mesma origem (o painel de
  // suporte usa `phada_admin_*`) — nao cabe a este app apaga-las.
  const PREFIXOS = ['session', 'config'];
  Object.keys(localStorage)
    .filter((chave) => PREFIXOS.some((p) => chave.startsWith(p)))
    .forEach((chave) => localStorage.removeItem(chave));

  localStorage.setItem('isLogged', JSON.stringify(false));
};

/**
 * Inicia monitoramento periodico do token
 * @param {function} onExpired - Callback quando token expira
 * @param {number} checkInterval - Intervalo de verificacao em ms (padrao: 60000)
 * @returns {number} - ID do interval para poder cancelar depois
 */
export const startTokenMonitor = (onExpired, checkInterval = 60000) => {
  const intervalId = setInterval(() => {
    const token = localStorage.getItem('sessionToken');

    if (token && isTokenExpired(token)) {
      clearSession();
      clearInterval(intervalId);
      if (onExpired) {
        onExpired();
      }
    }
  }, checkInterval);

  // Verificar imediatamente tambem
  const token = localStorage.getItem('sessionToken');
  if (token && isTokenExpired(token)) {
    clearSession();
    clearInterval(intervalId);
    if (onExpired) {
      setTimeout(onExpired, 0);
    }
  }

  return intervalId;
};

/**
 * Para o monitoramento do token
 * @param {number} intervalId - ID retornado por startTokenMonitor
 */
export const stopTokenMonitor = (intervalId) => {
  if (intervalId) {
    clearInterval(intervalId);
  }
};

// ==========================================
// REFRESH TOKEN INFRASTRUCTURE
// ==========================================
// Requer endpoint backend: POST /v1/auth/refresh
// Request body: { refreshToken: "..." }
// Response: { token: "novo_access_token", refreshToken: "novo_refresh_token" }

let isRefreshing = false;
let refreshSubscribers = [];

const onRefreshed = (newToken) => {
  refreshSubscribers.forEach(cb => cb(newToken));
  refreshSubscribers = [];
};

const onRefreshFailed = () => {
  refreshSubscribers.forEach(cb => cb(null));
  refreshSubscribers = [];
};

/**
 * Adiciona callback para ser notificado quando refresh completar
 * @param {function} callback - Recebe novo token ou null se falhou
 * @returns {Promise<string|null>}
 */
export const subscribeTokenRefresh = (callback) => {
  refreshSubscribers.push(callback);
};

/**
 * Verifica se o token está próximo de expirar (menos de 5 minutos)
 * @param {string} token - Token JWT
 * @returns {boolean}
 */
export const isTokenNearExpiry = (token) => {
  const remaining = getTokenRemainingTime(token);
  return remaining > 0 && remaining < 5 * 60 * 1000; // 5 minutos
};

/**
 * Tenta renovar o token usando o refresh token.
 * Se já há um refresh em andamento, aguarda o resultado.
 * @param {object} axiosInstance - Instância do axios (para evitar import circular)
 * @returns {Promise<string|null>} - Novo token ou null se falhou
 */
export const attemptTokenRefresh = async (axiosInstance) => {
  const refreshToken = localStorage.getItem('sessionRefreshToken');

  if (!refreshToken) {
    return null;
  }

  // Se já está refreshing, esperar resultado
  if (isRefreshing) {
    return new Promise((resolve) => {
      subscribeTokenRefresh(resolve);
    });
  }

  isRefreshing = true;

  try {
    const { data } = await axiosInstance.post('/v1/auth/refresh', {
      refreshToken,
    }, {
      skipGlobalErrorToast: true,
      isRefreshRequest: true,
    });

    if (data.token) {
      localStorage.setItem('sessionToken', data.token);
      if (data.refreshToken) {
        localStorage.setItem('sessionRefreshToken', data.refreshToken);
      }
      onRefreshed(data.token);
      return data.token;
    }

    onRefreshFailed();
    return null;
  } catch {
    onRefreshFailed();
    localStorage.removeItem('sessionRefreshToken');
    return null;
  } finally {
    isRefreshing = false;
  }
};

/**
 * @returns {boolean} true se há refresh em andamento
 */
export const isRefreshingToken = () => isRefreshing;

/**
 * Extrai dados do usuario do token
 * @param {string} token - Token JWT (opcional, usa localStorage)
 * @returns {object|null} - Dados do usuario ou null
 */
export const getUserFromToken = (token) => {
  const tokenToCheck = token || localStorage.getItem('sessionToken');
  const decoded = decodeJWT(tokenToCheck);

  if (!decoded) return null;

  return {
    id: decoded.ID_USUARIO || decoded.id_usuario || decoded.idusuario || decoded.id,
    clienteId: decoded.ID_CLIENTE || decoded.id_cliente || decoded.idcliente,
    lojaId: decoded.ID_LOJA || decoded.id_loja || decoded.idloja,
  };
};
